import React from "react";
import { ThunkDispatch } from "redux-thunk";
import { showSnack } from "react-redux-snackbar";
import { connect } from "react-redux";
import { endpoints } from "../../..";
import { ApiRequest, IAppState } from "../../../../Common";
import { FeedbackPriority } from "../../../Constants/feedbackEnums";
import FeedbackPrioritySetter from "./FeedbackPrioritySetter";

type Props = {
  feedbackCode: string;
  existingPriority?: FeedbackPriority;
  includeLabel?: boolean;
  saveImmediately?: boolean;
  isReadonly: boolean;
  onChangePriority?: (feedbackCode: string, priority: FeedbackPriority) => void;
};

type DispatchProps = {
  showSnack: (uniqueId: any, args: any) => void;
};

type State = {
  selectedPriority?: FeedbackPriority;
  priorityOptions: FeedbackPriority[];
};

function mapDispatchToProps(dispatch: ThunkDispatch<IAppState, {}, any>): DispatchProps {
  return {
    showSnack: (uniqueId: any, args: any) => dispatch(showSnack(uniqueId, args))
  };
}

class FeedbackPrioritySetterContainer extends React.Component<Props & DispatchProps, State> {
  private changePriorityLock: boolean;

  constructor(props: Props & DispatchProps) {
    super(props);

    this.state = {
      selectedPriority: props.existingPriority,
      priorityOptions: Object.keys(FeedbackPriority)
        .filter(k => typeof FeedbackPriority[k as any] === "number")
        .map(k => FeedbackPriority[k as keyof typeof FeedbackPriority])
    };

    this.changePriorityLock = false;

    this.onSelectPriority = this.onSelectPriority.bind(this);
  }

  onSelectPriority(priority: FeedbackPriority) {
    // check if we need to save
    if (this.props.saveImmediately) {
      // save changes
      this.saveChanges(priority);
    } else {
      // just set the state and call the callback if there is one
      this.setState(state => {
        return { ...state, selectedPriority: priority };
      });
      if (this.props.onChangePriority) {
        this.props.onChangePriority(this.props.feedbackCode, priority);
      }
    }
  }

  saveChanges(priority: FeedbackPriority) {
    if (!this.claimPriorityLock()) {
      return;
    }

    // POST vote to server
    const { onChangePriority } = this.props;
    const setState = this.setState.bind(this);
    const releaseLock = this.releasePriorityLock.bind(this);

    const body = {
      Priority: priority
    };

    ApiRequest.fromPath(endpoints.feedback.priority(this.props.feedbackCode))
      .withJson(body)
      .withAuthHeader()
      .put()
      .then(response => {
        if (response.status === 200) {
          if (onChangePriority) {
            onChangePriority(this.props.feedbackCode, priority);
          }

          setState(state => {
            return { ...state, selectedPriority: priority };
          });
          this.showPostSetPrioritySnackbar(priority);
        } else {
          response.json().then(json => {
            this.showErrorSnackbar(response.status, json.title);
          });
        }
        releaseLock();
      });
  }

  showPostSetPrioritySnackbar(priority: FeedbackPriority) {
    const label = `Set feedback priority to "${priority}"`;
    this.props.showSnack("FEEDBACK_PRIORITY", {
      label,
      timeout: 2000
    });
  }

  showErrorSnackbar(errorCode: number, errorDescription: string) {
    this.props.showSnack("FEEDBACK_PRIORITY", {
      label: `Failed to set feedback priority: ${errorDescription} (Error Code ${errorCode})`,
      timeout: 5000
    });
  }

  claimPriorityLock() {
    if (!this.changePriorityLock) {
      this.changePriorityLock = true;
      return true;
    }
    return false;
  }

  releasePriorityLock() {
    this.changePriorityLock = false;
  }

  render() {
    return (
      <FeedbackPrioritySetter
        selectedPriority={this.state.selectedPriority}
        priorityOptions={this.state.priorityOptions}
        includeLabel={this.props.includeLabel ? this.props.includeLabel : false}
        isReadOnly={this.props.isReadonly}
        onSelectPriority={this.onSelectPriority}
      />
    );
  }
}

export default connect(
  null,
  mapDispatchToProps
)(FeedbackPrioritySetterContainer);
